import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import useAuth from "../hooks/useAuth";
import { hasValidContext } from "../utils/contextStorage";
import { getDashboardPathForRole } from "../utils/dashboardRouting";

const Sidebar = () => {
    const { roleId, role } = useAuth();
    const location = useLocation();

    const [menus, setMenus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isContextValid, setIsContextValid] = useState(false);
    const [openIds, setOpenIds] = useState(new Set());

    useEffect(() => {
        const checkContext = () => setIsContextValid(hasValidContext());
        checkContext();
        window.addEventListener("storage", checkContext);
        return () => window.removeEventListener("storage", checkContext);
    }, []);

    useEffect(() => {
        if (!roleId) return;
        setLoading(true);
        axiosInstance
            .get(`/api/role-page-permissions/role/${roleId}/view-menus`)
            .then((res) => {
                const data = res.data || [];
                setMenus(data);

                // Auto-open the parent menu that contains the active page
                const activeParentId = data.find((m) =>
                    (m.children || []).some((c) => location.pathname === resolveMenuPath(c))
                )?.id;
                if (activeParentId) {
                    setOpenIds(new Set([activeParentId]));
                }
            })
            .catch((err) => console.error("Failed to load sidebar menus", err))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleId]);

    const shouldDisableMenus = useMemo(() => {
        if (roleId === 1) return false;
        if (roleId === 4 && !isContextValid) return true;
        return false;
    }, [roleId, isContextValid]);

    const resolveMenuPath = (menu) => {
        const path = String(menu?.path || "").trim();
        const name = String(menu?.name || "").trim().toLowerCase();
        if (path === "/dashboard" || name === "dashboard") return getDashboardPathForRole(role);
        return path;
    };

    const toggleMenu = (id) => {
        setOpenIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    if (loading) return <div className="sidebar-loading">Loading menu...</div>;

    const parentMenus = menus.filter((m) => m.parentId === null);

    return (
        <div className="leftside-menu">
            <ul className="side-nav">
                {parentMenus.map((menu) => {
                    const children = menu.children || [];
                    const resolvedPath = resolveMenuPath(menu);
                    const isOpen = openIds.has(menu.id);

                    const isActive =
                        location.pathname === resolvedPath ||
                        children.some((c) => location.pathname === resolveMenuPath(c));

                    if (children.length > 0) {
                        return (
                            <li
                                key={menu.id}
                                className={`side-nav-item ${isActive ? "menuitem-active" : ""}`}
                            >
                                <a
                                    href="#!"
                                    className="side-nav-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        toggleMenu(menu.id);
                                    }}
                                    aria-expanded={isOpen || isActive}
                                    style={{ cursor: "pointer" }}
                                >
                                    <span className="menu-icon">
                                        <i className={menu.icon}></i>
                                    </span>
                                    <span className="menu-text">{menu.name}</span>
                                    <span className="menu-arrow" />
                                </a>

                                <div
                                    style={{
                                        overflow: "hidden",
                                        maxHeight: (isOpen || isActive) ? "500px" : "0px",
                                        transition: "max-height 0.3s ease",
                                    }}
                                >
                                    <ul className="sub-menu">
                                        {children.map((child) => {
                                            const childPath = resolveMenuPath(child);
                                            const isChildActive = location.pathname === childPath;
                                            return (
                                                <li
                                                    key={child.id}
                                                    className={`side-nav-item ${isChildActive ? "menuitem-active" : ""}`}
                                                >
                                                    {shouldDisableMenus ? (
                                                        <span
                                                            className="side-nav-link disabled-menu"
                                                            style={{ opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }}
                                                        >
                                                            {child.name}
                                                        </span>
                                                    ) : (
                                                        <Link to={childPath} className="side-nav-link">
                                                            {child.name}
                                                        </Link>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </li>
                        );
                    }

                    /* Single menu (no children) */
                    return (
                        <li
                            key={menu.id}
                            className={`side-nav-item ${location.pathname === resolvedPath ? "menuitem-active" : ""}`}
                        >
                            <Link to={resolvedPath} className="side-nav-link">
                                <span className="menu-icon">
                                    <i className={menu.icon}></i>
                                </span>
                                <span className="menu-text">{menu.name}</span>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default Sidebar;
